import { useCallback, useEffect, useMemo, useState } from 'react'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { RecordFactory } from '../../models/RecordFactory'
import { User } from '../../models/User'
import { PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import SegmentedControl from '../app/design/SegmentedControl'
import { ClientSearchableSelect } from '../contacts/ClientSearchableSelect'
import { ComboBox, ComboBoxItem } from '../common/fields/ComboBox'
import { SearchableSelect } from '../common/fields/SearchableSelect'
import { Select } from '../common/fields/Select'
import { WorkflowPage } from '../workflows/WorkflowPage'
import { dataExportDateFilterOptions } from './Options'

const clientSelectTypes = [
    { label: 'All clients', value: 'all' },
    { label: 'Single client', value: 'single' },
]

const render = ({
    base: { me: user, owner, showProgressBar, hideProgressBar, logAndDisplayError },
}: PocketComponentProps<void>) => {
    const navigate = usePSNavigate()
    const [isProcessing, setIsProcessing] = useState(false)
    const [dateFilter, setDateFilter] = useState(dataExportDateFilterOptions[1])
    const [client, setClient] = useState<User | undefined>(undefined)
    const [selectedExportTypes, setSelectedExportTypes] = useState<ComboBoxItem[]>([])
    const [showLearnMoreModal, setShowLearnMoreModal] = useState(false)
    const [clientSelectType, setClientSelectType] =
        useState<(typeof clientSelectTypes)[number]['value']>('all')
    const [exportTypeSearchValue, setExportTypeSearchValue] = useState('')
    const [showExportComplete, setShowExportComplete] = useState(false)

    const exportTypes: ComboBoxItem[] = useMemo(() => {
        const exports = []
        if (['company', 'solo', 'payee'].includes(user.type!)) {
            exports.push(
                ...[
                    { title: 'Payments', id: 'payments' },
                    { title: 'Invoices', id: 'invoices' },
                    { title: 'Appointments', id: 'bookings' },
                    { title: 'Clients', id: 'clients' },
                    { title: 'Active clients', id: 'active' },
                    { title: 'Paying clients', id: 'paying' },
                    { title: 'Notes', id: 'notes' },
                    { title: 'Item purchased', id: 'items' },
                ]
            )
            if (owner.allowProduct) exports.push({ title: 'Inventory', id: 'inventory' })
            if (owner.allowContract) exports.push({ title: 'Contracts', id: 'contracts' })
            if (owner.allowRecord) exports.push({ title: 'Forms', id: 'records' })
            if (owner.allowStaffing) exports.push({ title: 'Staffing', id: 'payees' })
            if (owner.allowClass) exports.push({ title: 'Classes', id: 'classes' })
            if (owner.allowPackage) exports.push({ title: 'Packages', id: 'packages' })
            if (owner.allowSubscription)
                exports.push({ title: 'Subscriptions', id: 'subscriptions' })
            if (owner.allowGift) exports.push({ title: 'Gift certificates', id: 'gifts' })
            if (owner.allowEstimate) exports.push({ title: 'Estimates', id: 'estimates' })
            if (owner.allowTrigger) exports.push({ title: 'Smart campaigns', id: 'triggers' })
            if (owner.allowTask) exports.push({ title: 'Tasks', id: 'tasks' })
        }
        if (owner.allowPost) {
            exports.push({ title: 'Posts', id: 'posts' })
            exports.push({ title: 'Members', id: 'subscribers' })
        }
        return exports
    }, [user, owner])

    const onSend = useCallback(async () => {
        setIsProcessing(true)
        const result = await RecordFactory.getInstance().submitDataExport(
            user,
            client,
            selectedExportTypes.map((type) => type.id.toString()),
            dateFilter.value
        )
        setIsProcessing(false)

        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
        setShowExportComplete(true)
    }, [user, client, selectedExportTypes, dateFilter])

    const handleSelectExportType = (item: ComboBoxItem | null) => {
        if (item === null) {
            return
        }

        const itemAlreadySelected = selectedExportTypes.some(
            (selectedItem) => selectedItem.id === item.id
        )

        if (itemAlreadySelected) {
            setSelectedExportTypes(
                selectedExportTypes.filter((selectedItem) => selectedItem.id !== item.id)
            )
        }
        else {
            setSelectedExportTypes([...selectedExportTypes, item])
        }
    }

    const handleClientSelect = (user: User | undefined) => {
        if (!user) return

        if (user.id === client?.id) {
            setClient(undefined)
        }
        else {
            setClient(user)
        }
    }

    const timePeriodItems = dataExportDateFilterOptions.map((option) => ({
        id: option.value,
        title: option.label,
    }))
    const exportTypeItems = useMemo(() => {
        return exportTypes.map((item) => ({
            ...item,
            isChecked: selectedExportTypes.some((selectedType) => item.id === selectedType.id),
        }))
    }, [exportTypes, selectedExportTypes])

    const deselectItemWithID = (id: string | number) => {
        setSelectedExportTypes(selectedExportTypes.filter((item) => item.id !== id))
    }

    return (
        <>
            <WorkflowPage
                title="Data Export"
                footerContent={
                    <div className="flex w-full items-center justify-end gap-x-4">
                        <Button intent="secondary" onClick={() => setShowLearnMoreModal(true)}>
                            Learn more
                        </Button>
                        <Button intent="primary" onClick={onSend} isLoading={isProcessing}>
                            Send
                        </Button>
                    </div>
                }
            >
                <div className="mt-12 flex w-full flex-col items-center justify-start">
                    <WorkflowPage.Card>
                        <WorkflowPage.IconSection title="Time Period" icon="calendar.large">
                            <SearchableSelect
                                size="medium"
                                items={timePeriodItems}
                                onSelectItem={(selection) => {
                                    const selectedOption = dataExportDateFilterOptions.find(
                                        (option) => option.value === selection?.id
                                    )
                                    if (selectedOption) {
                                        setDateFilter(selectedOption)
                                    }
                                }}
                                selectedItemID={dateFilter.value}
                                shouldPreserveLastSelection
                            />
                        </WorkflowPage.IconSection>

                        <WorkflowPage.IconSection title="Client" icon="user.large">
                            <SegmentedControl
                                segments={clientSelectTypes}
                                value={clientSelectType}
                                onChange={(value) => setClientSelectType(value)}
                                size="medium"
                            />
                            {clientSelectType === 'single' && (
                                <ClientSearchableSelect
                                    mode="server"
                                    selectedClient={client}
                                    onSelectClient={handleClientSelect}
                                />
                            )}
                        </WorkflowPage.IconSection>

                        <WorkflowPage.IconSection title="Export types" icon="settings.large">
                            <ComboBox
                                items={exportTypeItems}
                                onSelectItem={handleSelectExportType}
                                searchValue={exportTypeSearchValue}
                                onSearchValueChange={setExportTypeSearchValue}
                                placeholder="Select"
                                size="medium"
                                chips={selectedExportTypes}
                                shouldRefocusAfterSelection
                                onRemoveChipWithID={deselectItemWithID}
                            />
                        </WorkflowPage.IconSection>
                    </WorkflowPage.Card>
                </div>
            </WorkflowPage>

            <Modal
                isOpen={showLearnMoreModal}
                title="Data Export"
                onClose={() => setShowLearnMoreModal(false)}
                footer={
                    <div className="flex w-full items-center justify-end gap-x-4">
                        <Button intent="primary" onClick={() => setShowLearnMoreModal(false)}>
                            Close
                        </Button>
                    </div>
                }
            >
                Data export lets you download your entire transaction history for your records. An
                email will be sent to you once the export is completed with download instructions.
                Open this email from a computer. The attached CSV/Excel file in the email will not
                open on a mobile device.
            </Modal>

            <Modal
                isOpen={showExportComplete}
                title="Success"
                onClose={() => navigate('/settings')}
                footer={
                    <div className="flex w-full items-center justify-end gap-x-4">
                        <Button intent="primary" onClick={() => navigate('/settings')}>
                            Done
                        </Button>
                    </div>
                }
            >
                Export completed! Your data was emailed to {user?.email}
            </Modal>
        </>
    )
}

export const SetupDataExportPage = usePSComponent(render)
